﻿import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@pms/shared';
import { CalendarDays, CheckSquare, LogIn } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { StatCard } from '@/components/ui/stat-card';
import { attendanceService } from '@/services/attendance.service';
import { leaveService } from '@/services/leave.service';
import { tasksService } from '@/services/tasks.service';

// Salary is deliberately absent from this row. The dashboard is the first thing on
// screen — and often on a shared or overlooked one — so the figure lives behind a
// deliberate navigation instead: My Account → Salary (/account/salary).

/** Shared by the skeleton and the real row so the two can never disagree. */
const KPI_GRID_CLS = 'grid grid-cols-2 gap-3 lg:grid-cols-3';

function KpiSkeleton() {
  return (
    <div className={KPI_GRID_CLS}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-[100px] animate-pulse rounded-xl bg-muted/50" />
      ))}
    </div>
  );
}

export function UserKPIs() {
  const { user } = useAuth();
  const { has } = usePermission();

  const todayStatusQ = useQuery({
    queryKey: ['dashboard', 'user-kpi', 'today-status'],
    queryFn: () => attendanceService.getTodayStatus(),
    enabled: has(PERMISSIONS.ATTENDANCE_PUNCH),
  });

  const balanceQ = useQuery({
    queryKey: ['dashboard', 'user-kpi', 'leave-balance', user?.id],
    queryFn: () => leaveService.getMyBalances(),
    enabled: !!user?.id,
  });

  const myTasksQ = useQuery({
    queryKey: ['dashboard', 'user-kpi', 'my-tasks'],
    queryFn: () => tasksService.myTasks(),
  });

  const isLoading =
    todayStatusQ.isLoading || balanceQ.isLoading || myTasksQ.isLoading;

  if (isLoading) return <KpiSkeleton />;

  // Status follows the LATEST punch. The daily record keeps last_punch_out set after an
  // employee checks back in (e.g. after lunch), so reading it showed "Checked Out" all afternoon.
  const punches = todayStatusQ.data?.punches ?? [];
  const lastPunch = punches[punches.length - 1];
  let statusValue: string;
  let statusHint: string;
  if (lastPunch?.punch_type === 'check_in') {
    statusValue = 'Checked In';
    statusHint = `Since ${new Date(lastPunch.punch_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } else if (lastPunch?.punch_type === 'check_out') {
    statusValue = 'Checked Out';
    statusHint = `At ${new Date(lastPunch.punch_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } else {
    statusValue = 'Not In';
    statusHint = 'Not checked in today';
  }

  const totalLeave = (balanceQ.data ?? []).reduce((sum, b) => sum + Number(b.available ?? 0), 0);

  const tasks = myTasksQ.data ?? [];
  const pendingTasks = tasks.filter((t) => t.display_status !== 'completed' && t.display_status !== 'discarded').length;

  return (
    <div className={KPI_GRID_CLS}>
      <StatCard
        label="Today's Status"
        value={statusValue}
        hint={statusHint}
        icon={LogIn}
        tone="teal"
      />
      <StatCard
        label="Leave Balance"
        value={totalLeave}
        hint="Total remaining days"
        icon={CalendarDays}
        tone="emerald"
      />
      <StatCard
        label="Pending Tasks"
        value={pendingTasks}
        hint="Active tasks assigned to you"
        icon={CheckSquare}
        tone="amber"
      />
    </div>
  );
}
